import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useLeads, Lead } from '@/hooks/useLeads';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { completeLead } from '@/lib/leads/actions';
import { errorMessage } from '@/lib/errors';
import { LeadsTable } from '@/components/leads/LeadsTable';
import { LeadsFilters, LeadsFilterState } from '@/components/leads/LeadsFilters';
import { CreateLeadModal } from '@/components/leads/CreateLeadModal';
import { EditLeadModal } from '@/components/leads/EditLeadModal';
import { LeadDetailsDrawer } from '@/components/leads/LeadDetailsDrawer';
import { DeleteLeadDialog } from '@/components/leads/DeleteLeadDialog';
import { CSVImportModal } from '@/components/leads/CSVImportModal';
import { CreateDealModal } from '@/components/leads/CreateDealModal';
import { EmptyState } from '@/components/EmptyState';
import { LoadingState } from '@/components/LoadingState';
import { PageHeader } from '@/components/ui/page-header';
import { DataTableWrapper } from '@/components/ui/data-table-wrapper';
import { TableSkeleton } from '@/components/ui/skeletons';
import { Button } from '@/components/ui/button';
import { ProButton } from '@/components/ui/pro-button';
import { Badge } from '@/components/ui/badge';
import { Plus, Upload, Users, X } from 'lucide-react';
import { differenceInDays } from 'date-fns';

export default function Leads() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: leads, isLoading, error } = useLeads();
  const { mode, activeWorkspaceId, isLoading: workspaceLoading } = useWorkspaceScope();
  
  // Debug logging in development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Leads] Render state:', {
        leadsCount: leads?.length ?? 'undefined',
        isLoading,
        error: error?.message,
        activeWorkspaceId,
        workspaceLoading,
      });
    }
  }, [leads, isLoading, error, activeWorkspaceId, workspaceLoading]);
  
  // Check for special filters from URL
  const urlFilter = searchParams.get('filter');
  const urlDays = parseInt(searchParams.get('days') || '7', 10);
  const leadIdFromUrl = searchParams.get('id');
  
  const [filters, setFilters] = useState<LeadsFilterState>({
    search: '',
    temperature: 'all',
    stage: 'all',
    source: 'all',
    followUpStatus: 'all',
  });
  
  // Check URL params for action (create/import)
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'create') {
      setCreateOpen(true);
      // Clear the param after opening
      setSearchParams({}, { replace: true });
    } else if (action === 'import') {
      setImportOpen(true);
      // Clear the param after opening
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [createDealOpen, setCreateDealOpen] = useState(false);
  const [highlightedLeadId, setHighlightedLeadId] = useState<string | null>(null);

  // Clear URL filter when user manually changes filters
  const handleFiltersChange = (newFilters: LeadsFilterState) => {
    setFilters(newFilters);
    // Clear URL params when user changes filters
    if (urlFilter) {
      setSearchParams({});
    }
  };

  const clearUrlFilter = () => {
    setSearchParams({});
  };

  const filteredLeads = useMemo(() => {
    if (!leads) return [];
    
    let result = leads;

    // Apply URL-based special filters
    if (urlFilter === 'stale') {
      const now = new Date();
      result = leads.filter(lead => {
        if (lead.pipeline_stage === 'won' || lead.pipeline_stage === 'lost') return false;
        if (!lead.last_interaction_at) return true;
        return differenceInDays(now, new Date(lead.last_interaction_at)) > urlDays;
      });
    }
    
    // Then apply regular filters
    return result.filter(lead => {
      // Search filter
      if (filters.search) {
        const search = filters.search.toLowerCase();
        const matchesSearch = 
          lead.name.toLowerCase().includes(search) ||
          lead.email?.toLowerCase().includes(search) ||
          lead.phone?.includes(search) ||
          lead.notes_short?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Temperature filter
      if (filters.temperature !== 'all' && lead.temperature !== filters.temperature) {
        return false;
      }
      
      // Stage filter
      if (filters.stage !== 'all' && lead.pipeline_stage !== filters.stage) {
        return false;
      }
      
      // Source filter
      if (filters.source !== 'all' && lead.source !== filters.source) {
        return false;
      }
      
      // Follow-up status filter
      if (filters.followUpStatus !== 'all') {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);
        
        const followUp = lead.next_action_at ? new Date(lead.next_action_at) : null;
        
        switch (filters.followUpStatus) {
          case 'overdue':
            if (!followUp || followUp >= today) return false;
            break;
          case 'today':
            if (!followUp || followUp < today || followUp >= tomorrow) return false;
            break;
          case 'upcoming':
            if (!followUp || followUp < tomorrow || followUp >= nextWeek) return false;
            break;
          case 'none':
            if (followUp) return false;
            break;
        }
      }
      
      return true;
    });
  }, [leads, filters, urlFilter, urlDays]);

  const handleViewLead = (lead: Lead) => {
    setSelectedLead(lead);
    setDrawerOpen(true);
  };

  const handleEditLead = (lead: Lead) => {
    setSelectedLead(lead);
    setEditOpen(true);
  };

  const handleDeleteLead = (lead: Lead) => {
    setSelectedLead(lead);
    setDeleteOpen(true);
  };

  const handleEditFromDrawer = () => {
    setDrawerOpen(false);
    setEditOpen(true);
  };

  const handleCreateDeal = (lead: Lead) => {
    setSelectedLead(lead);
    setCreateDealOpen(true);
  };

  const handleMarkDone = async (lead: Lead) => {
    const workspaceId = lead.workspace_id ?? activeWorkspaceId;
    if (!workspaceId) {
      toast.error('No workspace');
      return;
    }
    try {
      await completeLead(lead.id, workspaceId);
      queryClient.invalidateQueries({ queryKey: ['leads'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['elite-today-plan'], exact: false });
      toast.success('Marked done');
    } catch (err: any) {
      toast.error(errorMessage(err, 'Failed to mark done'));
    }
  };

  // Handle lead ID from URL - highlight and open drawer
  useEffect(() => {
    if (leadIdFromUrl && leads) {
      const lead = leads.find(l => l.id === leadIdFromUrl);
      if (lead) {
        setSelectedLead(lead);
        setDrawerOpen(true);
        setHighlightedLeadId(leadIdFromUrl);
        
        // Scroll to lead in table after a short delay (to ensure table is rendered)
        setTimeout(() => {
          const element = document.getElementById(`lead-row-${leadIdFromUrl}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Remove highlight after scroll animation
            setTimeout(() => setHighlightedLeadId(null), 2000);
          }
        }, 300);
      }
    }
  }, [leadIdFromUrl, leads]);

  // Show loading while workspace is initializing or leads are loading
  if (workspaceLoading || isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <PageHeader
          title="Leads"
          description="Loading leads..."
        />
        <div className="mt-8">
          <TableSkeleton rows={8} columns={7} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          icon={Users}
          title="Something went wrong"
          description="We couldn't load your leads. Please try again."
          action={
            <ProButton variant="primary" onClick={() => window.location.reload()}>
              Try Again
            </ProButton>
          }
        />
      </div>
    );
  }

  // If no workspace is active, show message
  if (!activeWorkspaceId && mode !== 'all') {
    return (
      <div className="container mx-auto px-4 py-8">
        <EmptyState
          icon={Users}
          title="No workspace selected"
          description="Please select a workspace to view leads."
        />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
      className="container mx-auto px-4 py-8"
    >
      {/* Header */}
      <PageHeader
        title="Leads"
        description={`${leads?.length || 0} total leads`}
                  actions={
                    <>
                      <ProButton variant="outline" size="sm" icon={Upload} onClick={() => setImportOpen(true)}>
                        Import CSV
                      </ProButton>
                      <ProButton variant="primary" size="sm" icon={Plus} onClick={() => setCreateOpen(true)}>
                        Add Lead
                      </ProButton>
                    </>
                  }
      />

      {/* URL Filter Banner */}
      {urlFilter && (
        <motion.div
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
          className="mb-4 flex items-center gap-2 p-3 rounded-xl bg-primary/5 border border-primary/20"
        >
          <Badge variant="secondary" className="gap-1">
            {urlFilter === 'stale' && `Stale (no activity in ${urlDays}+ days)`}
          </Badge>
          <span className="text-sm text-muted-foreground">
            Showing {filteredLeads.length} leads
          </span>
          <ProButton
            variant="ghost"
            size="xs"
            icon={X}
            onClick={clearUrlFilter}
            className="ml-auto"
          >
            Clear Filter
          </ProButton>
        </motion.div>
      )}

      {/* Filters and Table */}
      <DataTableWrapper
        filters={
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, delay: 0.05 }}
          >
            <LeadsFilters filters={filters} onChange={handleFiltersChange} />
          </motion.div>
        }
      >
        {leads === undefined || leads === null ? (
          // Still loading - show skeleton inside wrapper
          <div className="p-8">
            <TableSkeleton rows={5} columns={7} />
          </div>
        ) : leads.length === 0 ? (
          // No leads in database
          <div className="p-8">
            <EmptyState
              icon={Users}
              title="No leads yet"
              description="Start by adding your first lead or importing from a CSV file."
              action={
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button onClick={() => setCreateOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Lead
                  </Button>
                  <Button variant="outline" onClick={() => setImportOpen(true)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Import CSV
                  </Button>
                </div>
              }
            />
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={Users}
              title="No matching leads"
              description={urlFilter 
                ? `No ${urlFilter} leads found with current criteria.`
                : "Try adjusting your filters to find what you're looking for."
              }
                        action={
                          <ProButton
                            variant="outline"
                            onClick={() => {
                              setFilters({
                                search: '',
                                temperature: 'all',
                                stage: 'all',
                                source: 'all',
                                followUpStatus: 'all',
                              });
                              clearUrlFilter();
                            }}
                          >
                            Clear All Filters
                          </ProButton>
                        }
            />
          </div>
        ) : (
          <LeadsTable
            leads={filteredLeads}
            onView={handleViewLead}
            onEdit={handleEditLead}
            onDelete={handleDeleteLead}
            onCreateDeal={handleCreateDeal}
            onMarkDone={handleMarkDone}
            highlightedLeadId={highlightedLeadId}
            showWorkspaceColumn={mode === 'all'}
          />
        )}
      </DataTableWrapper>

      {/* Modals & Drawers */}
      <CreateLeadModal open={createOpen} onOpenChange={setCreateOpen} />
      <EditLeadModal lead={selectedLead} open={editOpen} onOpenChange={setEditOpen} />
      <LeadDetailsDrawer
        lead={selectedLead}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onEdit={handleEditFromDrawer}
      />
      <DeleteLeadDialog lead={selectedLead} open={deleteOpen} onOpenChange={setDeleteOpen} />
      <CSVImportModal open={importOpen} onOpenChange={setImportOpen} />
      {selectedLead && (
        <CreateDealModal 
          lead={selectedLead} 
          open={createDealOpen} 
          onOpenChange={setCreateDealOpen} 
        />
      )}
    </motion.div>
  );
}
